import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, X } from 'lucide-react';
import { useAdminNotifications } from '../../context/AdminNotificationContext';

const SEVERITY_DOT = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-gray-400',
};

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function AdminNotificationBell() {
  const navigate = useNavigate();
  const { unread, recent, refresh, readOne, readAll } = useAdminNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleOpen = (url, alertId, isRead) => {
    setOpen(false);
    if (!isRead && alertId) readOne(alertId).catch(() => {});
    if (url) navigate(url);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
        data-testid="admin-notification-bell"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[min(100vw-2rem,380px)] bg-white rounded-xl shadow-xl border border-gray-200 z-[60] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Alerts</h3>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => readAll()}
                  className="p-1.5 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-50"
                  title="Mark all read"
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 text-gray-500 hover:bg-gray-50 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {recent.length === 0 ? (
              <p className="px-4 py-8 text-sm text-gray-500 text-center">No alerts yet</p>
            ) : (
              recent.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => handleOpen(a.action_url, a.id, a.read_at)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                    !a.read_at ? 'bg-red-50/40' : ''
                  }`}
                >
                  <div className="flex gap-2">
                    <span
                      className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                        SEVERITY_DOT[a.severity] || SEVERITY_DOT.info
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{a.title}</p>
                      <p className="text-xs text-gray-600 line-clamp-2">{a.body}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{formatWhen(a.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
            <Link
              to="/admin/alerts"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-red-600 hover:text-red-700"
            >
              View all alerts
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
